import React, { useState, useEffect, useCallback } from 'react';
import JsonEditorForm from './JsonEditorForm';
import CMSAnalyticsDashboard from './CMSAnalyticsDashboard';

// Helper function for immutable updates
const updateNestedValue = (obj, path, value) => {
  if (!path) return value; // If path is empty, replace the whole object
  const keys = path.split('.');
  let current = { ...obj };
  let pointer = current;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    
    if (pointer[key] === undefined || typeof pointer[key] !== 'object' || pointer[key] === null) {
      // If path doesn't exist or is not an object, create objects along the path.
      // This might be too aggressive if we expect paths to always exist.
      // For this CMS, it should be fine as we are editing existing structures.
      pointer[key] = {}; 
    } else if (Array.isArray(pointer[key])) {
      // CRITICAL FIX: Preserve array structure!
      pointer[key] = [...pointer[key]]; // Shallow clone array
    } else {
      // Normal object shallow clone
      pointer[key] = { ...(pointer[key]) }; 
    }
    
    pointer = pointer[key];
  }
  
  // Final assignment
  const finalKey = keys[keys.length - 1];
  
  // CRITICAL FIX: Handle array index assignments
  if (Array.isArray(pointer) && /^\d+$/.test(finalKey)) {
    // If we're setting an array index, convert to number and preserve array structure
    const index = parseInt(finalKey, 10);
    pointer[index] = value;
  } else {
    // Normal object property assignment
    pointer[finalKey] = value;
  }
  
  return current;
};

// Simplified deep equal, use with caution for complex objects or specific needs.
// Key order in stringify can be an issue, but for typical JSON it often works.
const deepEqual = (obj1, obj2) => {
  if (obj1 === null && obj2 === null) return true;
  if (obj1 === null || obj2 === null) return false;
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

const CMSDashboardPage = ({ onLogout, initialTab = 'translations' }) => {
  const [versionsData, setVersionsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionFiles, setVersionFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFileContent, setEditingFileContent] = useState(null);
  const [originalFileContent, setOriginalFileContent] = useState(null);
  const [isContentDirty, setIsContentDirty] = useState(false);
  const [isLoadingFileContent, setIsLoadingFileContent] = useState(false);
  const [expandedLangs, setExpandedLangs] = useState({});
  const [focusedFieldPath, setFocusedFieldPath] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [lastFocusedFieldForScroll, setLastFocusedFieldForScroll] = useState(null);
  const [isUpdatingLiveVersion, setIsUpdatingLiveVersion] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Handle tab switching with URL updates
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    
    // Update URL without page reload
    const newUrl = newTab === 'translations' ? '/fay/translations' : '/fay/analytics';
    window.history.pushState(null, '', newUrl);
  };

  useEffect(() => {
    const fetchVersions = async (selectVersionAfterFetch = null) => {
      setIsLoading(true);
      setError(null);
      const cmsKey = localStorage.getItem('cmsKey');

      if (!cmsKey) {
        setError('CMS key not found. Please log in again.');
        setIsLoading(false);
        if (typeof onLogout === 'function') onLogout();
        return;
      }

      try {
        const response = await fetch('/api/cms/versions', {
          headers: { 'X-CMS-Key': cmsKey },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to fetch versions' }));
          throw new Error(`Error ${response.status}: ${errorData.error || response.statusText}`);
        }
        const data = await response.json();
        setVersionsData(data);

        if (selectVersionAfterFetch) {
            setSelectedVersion(selectVersionAfterFetch);
            // If a specific version is to be selected (e.g. after creation), fetch its files
            fetchVersionFiles(selectVersionAfterFetch);
        } else if (data?.versionConfig?.working && 
                   (!selectedVersion || !data.discoveredVersionNames?.includes(selectedVersion))) {
            // On initial load, if there's a working version and no user selection yet, select it
            const initialWorkingVersion = data.versionConfig.working;
            setSelectedVersion(initialWorkingVersion);
            // Crucially, also fetch its files immediately after setting it as selected
            if (initialWorkingVersion) {
                 fetchVersionFiles(initialWorkingVersion);
            }
        }
        
      } catch (err) {
        console.error("Error fetching versions:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLogout]);

  const fetchVersionFiles = async (versionName) => {
    if (!versionName) return;
    setIsLoadingFiles(true);
    setError(null);
    setVersionFiles([]);
    setExpandedLangs({});
    const cmsKey = localStorage.getItem('cmsKey');
    try {
      const response = await fetch(`/api/cms/version-files?versionName=${versionName}`, {
        headers: { 'X-CMS-Key': cmsKey },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch version files' }));
        throw new Error(`Error ${response.status}: ${errorData.error || response.statusText}`);
      }
      const data = await response.json();
      setVersionFiles(data.files || []);
      if (data.files && data.files.length > 0) {
        const initialExpandedState = (data.files || []).reduce((acc, file) => {
          const langKey = file.lang || 'root';
          acc[langKey] = false;
          return acc;
        }, {});
        setExpandedLangs(initialExpandedState);
      }
    } catch (err) {
      console.error(`Error fetching files for version ${versionName}:`, err);
      setError(err.message);
      setVersionFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const generateNewVersionName = (latestVersion) => {
    if (!latestVersion || !latestVersion.startsWith('v')) {
        return 'v001'; // Default if no latest or format is wrong
    }
    try {
        const numPart = parseInt(latestVersion.substring(1), 10);
        return `v${(numPart + 1).toString().padStart(3, '0')}`;
    } catch (e) {
        return 'v001'; // Fallback
    }
  };

  const handleCreateNewVersion = async () => {
    if (!versionsData || !versionsData.versionConfig || !versionsData.versionConfig.latest) {
        setError('Cannot determine the latest version to base the new one on. Please refresh.');
        return;
    }
    const sourceVersionName = versionsData.versionConfig.latest;
    const newVersionName = generateNewVersionName(sourceVersionName);

    if (!window.confirm(`Create new version "${newVersionName}" by copying all files from "${sourceVersionName}"?`)) {
        return;
    }

    setIsCreating(true);
    setError(null);
    const cmsKey = localStorage.getItem('cmsKey');

    try {
        const response = await fetch('/api/cms/create-version', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CMS-Key': cmsKey,
            },
            body: JSON.stringify({ newVersionName, sourceVersionName }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to create new version' }));
            throw new Error(`Error ${response.status}: ${errorData.error || response.statusText}`);
        }
        const result = await response.json();
        alert(result.message || 'New version created successfully!');
        // Re-fetch versions to update the list and select the new working version
        // The backend should set the new version as working
        const fetchVersions = async (selectVersionAfterFetch = null) => {
            setIsLoading(true);
            setError(null);
            const currentCmsKey = localStorage.getItem('cmsKey');
      
            if (!currentCmsKey) {
              setError('CMS key not found. Please log in again.');
              setIsLoading(false);
              if (typeof onLogout === 'function') onLogout();
              return;
            }
      
            try {
              const versionsResponse = await fetch('/api/cms/versions', {
                headers: { 'X-CMS-Key': currentCmsKey },
              });
              if (!versionsResponse.ok) {
                const errorData = await versionsResponse.json().catch(() => ({ error: 'Failed to fetch versions' }));
                throw new Error(`Error ${versionsResponse.status}: ${errorData.error || versionsResponse.statusText}`);
              }
              const data = await versionsResponse.json();
              setVersionsData(data);
      
              if (newVersionName) { // Select the newly created version
                  setSelectedVersion(newVersionName);
                  fetchVersionFiles(newVersionName);
              } else if (data?.versionConfig?.working && 
                         (!selectedVersion || !data.discoveredVersionNames?.includes(selectedVersion))) {
                  setSelectedVersion(data.versionConfig.working);
              }
              
            } catch (err) {
              console.error("Error fetching versions:", err);
              setError(err.message);
            } finally {
              setIsLoading(false);
            }
          };
        fetchVersions(newVersionName); 

    } catch (err) {
        console.error("Error creating new version:", err);
        setError(err.message);
    } finally {
        setIsCreating(false);
    }
  };

  const executePendingAction = () => {
    if (pendingAction) {
      pendingAction.action(...(pendingAction.args || []));
      setPendingAction(null);
    }
    setIsWarningModalOpen(false);
  };

  const handleWarningModalSave = async () => {
    if (!selectedFile || !editingFileContent) return; // Should not happen
    
    // Simplified save, assumes success for now for pending action execution
    // In a real scenario, you'd await actual save success
    setIsSaving(true);
    const cmsKey = localStorage.getItem('cmsKey');
    try {
      const response = await fetch('/api/cms/save-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CMS-Key': cmsKey },
        body: JSON.stringify({ r2Key: selectedFile.key, content: editingFileContent }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Save failed during warning modal');
      
      setNotification({ message: result.message || 'File saved successfully!', type: 'success', visible: true });
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 3000);
      setOriginalFileContent(JSON.parse(JSON.stringify(editingFileContent)));
      setIsContentDirty(false);
      executePendingAction(); // Execute the original action after successful save
    } catch (err) {
      console.error("Error saving from warning modal:", err);
      setError(`Save failed: ${err.message}. Please try saving manually or discard changes.`);
      // Do not close modal or execute pending action if save fails
      setNotification({ message: `Save failed: ${err.message}`, type: 'error', visible: true });
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleWarningModalDiscard = () => {
    setEditingFileContent(JSON.parse(JSON.stringify(originalFileContent))); // Revert
    setIsContentDirty(false);
    executePendingAction();
  };

  const handleWarningModalCancel = () => {
    setIsWarningModalOpen(false);
    setPendingAction(null);
  };

  const handleVersionClick = (versionName) => {
    const action = () => {
        setSelectedVersion(versionName);
        console.log("Selected version:", versionName);
        setVersionFiles([]);
        setSelectedFile(null);
        setEditingFileContent(null);
        setOriginalFileContent(null);
        setIsContentDirty(false);
        setFocusedFieldPath(null);
        setError(null);
        fetchVersionFiles(versionName);
    };

    if (isContentDirty) {
        setPendingAction({ action });
        setIsWarningModalOpen(true);
    } else {
        action();
    }
  };

  const handleBackToVersions = () => {
    const action = () => {
        setSelectedVersion(null);
        setVersionFiles([]);
        setSelectedFile(null);
        setEditingFileContent(null);
        setOriginalFileContent(null);
        setIsContentDirty(false);
        setFocusedFieldPath(null);
        setError(null);
        if (versionsData?.versionConfig?.working) {
            // setSelectedVersion(versionsData.versionConfig.working);
        }
    };
    if (isContentDirty) {
        setPendingAction({ action });
        setIsWarningModalOpen(true);
    } else {
        action();
    }
  };

  const actualFileClickLogic = async (file) => {
    setSelectedFile(file);
    setEditingFileContent(null); 
    setOriginalFileContent(null); // Clear before fetching
    setIsContentDirty(false); // Explicitly set to false before fetching
    setIsLoadingFileContent(true);
    setError(null); 
    console.log("Selected file for editing:", file);

    const cmsKey = localStorage.getItem('cmsKey');
    if (!cmsKey) {
      setError('CMS key not found. Please log in again.');
      setIsLoadingFileContent(false);
      if (typeof onLogout === 'function') onLogout();
      return;
    }
    try {
      const response = await fetch(`/api/cms/file-content?r2Key=${encodeURIComponent(file.key)}`, {
        headers: { 'X-CMS-Key': cmsKey },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch file content' }));
        throw new Error(`Error ${response.status}: ${errorData.error || response.statusText}`);
      }
      const content = await response.json();
      setEditingFileContent(content);
      setOriginalFileContent(JSON.parse(JSON.stringify(content)));
      setIsContentDirty(false); // Ensure it's false after loading new file
      setFocusedFieldPath(null); 
    } catch (err) {
      console.error(`Error fetching content for file ${file.key}:`, err);
      setError(`Failed to load content for ${file.fileName}: ${err.message}`);
      setEditingFileContent(null);
      setOriginalFileContent(null);
    } finally {
      setIsLoadingFileContent(false);
    }
  };

  const handleFileClick = async (file) => {
    if (isContentDirty && selectedFile && selectedFile.key !== file.key) {
      setPendingAction({ action: actualFileClickLogic, args: [file] });
      setIsWarningModalOpen(true);
    } else {
      actualFileClickLogic(file);
    }
  };

  const handleJsonDataChange = useCallback((path, value) => {
    setEditingFileContent(prevContent => {
      const newContent = updateNestedValue(prevContent, path, value);
      setIsContentDirty(!deepEqual(newContent, originalFileContent));
      return newContent;
    });
  }, [originalFileContent]);

  const handleRevertChanges = () => {
    if (window.confirm('Are you sure you want to revert all changes to this file?')) {
        setEditingFileContent(JSON.parse(JSON.stringify(originalFileContent)));
        setIsContentDirty(false);
        console.log('Content reverted to original.');
    }
  };

  const handleFocusField = useCallback((path) => {
    setFocusedFieldPath(path);
    // setLastFocusedFieldForScroll(path); // Don't set here, only before an action that reloads content
    console.log("Focused field path:", path);
  }, []);

  const handlePreviewClick = () => {
    if (!selectedFile) return;
    let baseUrl = window.location.origin;
    let path = ""; // Initialize path
    let hash = "";

    // Priority 1: Worker-specific pages
    if (selectedFile.namespace && selectedFile.namespace.startsWith("workers/")) {
      const workerName = selectedFile.namespace.substring("workers/".length);
      if (workerName) {
        path = `/digital-co-workers/${workerName}`;
      }
    } 
    // Priority 2: Specific page files (heuristic, adjust as needed)
    // Example: if you have a file named 'digital-workers.json' or 'in-actions.json'
    // at the root of a version (e.g., namespace is 'digital-workers' or 'in-actions')
    else if (selectedFile.namespace === "digital-workers") {
        path = "/digital-workers";
    } else if (selectedFile.namespace === "in-actions") {
        path = "/in-actions";
    } else if (selectedFile.namespace === "about-us" || selectedFile.fileName === "about.json") { // Assuming about.json might exist
        path = "/about-us";
    }
    // Priority 3: common.json and its internal linking logic
    else if (selectedFile.fileName === "common.json") {
      path = "/"; // Default for common.json is the homepage
      if (focusedFieldPath) {
        const topLevelKey = focusedFieldPath.split(".")[0];
        const sectionMap = {
          hero: "hero-section",
          about: "", // Special case, path becomes /about-us
          digitalWorkers: "digital-co-workers",
          platform: "platform",
          contact: "contact-us",
          mentor: "mentor-application",
        };

        if (sectionMap[topLevelKey] !== undefined) {
          if (topLevelKey === "about") {
            path = "/about-us"; // Override default path for common.json if focus is 'about'
          } else if (sectionMap[topLevelKey]) {
            hash = `#${sectionMap[topLevelKey]}`;
          }
          // If sectionMap[topLevelKey] is empty string (and not 'about'), it means homepage, no hash
        }
      }
    }

    // Fallback if no specific path could be determined (e.g., for other root files)
    if (!path) {
        path = "/"; // Default to homepage if no other rules match
    }

    // CORRECT URL CONSTRUCTION: query params BEFORE hash
    const baseUrlWithPath = `${baseUrl}${path}`;
    let finalPreviewUrl = `${baseUrlWithPath}${baseUrlWithPath.includes("?") ? "&" : "?"}cms_preview_version=${selectedVersion}`;
    finalPreviewUrl += `&iframe_cache_bust=${Date.now()}`;
    
    // Add hash at the very end
    if (hash) {
        finalPreviewUrl += hash;
    }

    console.log("[CMSDashboardPage] Generated Preview URL:", finalPreviewUrl);
    
    setPreviewUrl(finalPreviewUrl);
    setIsPreviewOpen(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedFile || !editingFileContent || !isContentDirty) {
        // This case should ideally not be met if button is disabled correctly
        alert("No changes to save, file not selected, or an error occurred.");
        return;
    }
    setIsSaving(true);
    setError(null);
    const cmsKey = localStorage.getItem('cmsKey');

    if (!cmsKey) {
        setError('CMS key not found. Please log in again.');
        setIsSaving(false);
        if (typeof onLogout === 'function') onLogout();
        return;
    }

    console.log("Saving content for:", selectedFile.key);

    try {
        const response = await fetch('/api/cms/save-file', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CMS-Key': cmsKey,
            },
            body: JSON.stringify({ 
                r2Key: selectedFile.key, 
                content: editingFileContent 
            }),
        });

        const result = await response.json(); // Try to parse JSON regardless of response.ok

        if (!response.ok) {
            throw new Error(result.error || `Error ${response.status}: Failed to save file`);
        }

        setNotification({ message: result.message || 'File saved successfully!', type: 'success', visible: true });
        setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 3000);

        // Update original content to current content, reset dirty flag
        setOriginalFileContent(JSON.parse(JSON.stringify(editingFileContent)));
        setIsContentDirty(false);

    } catch (err) {
        console.error("Error saving file:", err);
        setError(`Save failed: ${err.message}`);
        // Potentially, offer to retry or revert if save fails critically
    } finally {
        setIsSaving(false);
    }
  };

  const handleLanguageSwitch = () => {
    if (!selectedFile || !selectedFile.lang) return; // Only if file is language-specific
    if (!selectedFile.key) return; // Ensure key is present
    if (focusedFieldPath) {
        setLastFocusedFieldForScroll(focusedFieldPath); // Store focused field before switching
    }

    const currentLang = selectedFile.lang;
    const targetLang = currentLang === 'en' ? 'fr' : 'en';
    
    // Construct the new file key for the target language
    // Assumes structure like text/vXXX/en/namespace.json
    const keyParts = selectedFile.key.split('/');
    let newFileKey = '';
    if (keyParts.length > 2 && keyParts[keyParts.length - 2] === currentLang) {
        newFileKey = selectedFile.key.replace(`/${currentLang}/`, `/${targetLang}/`);
    } else {
        console.error("Cannot determine new file key for language switch. Key structure unexpected:", selectedFile.key);
        setError("Could not switch language: file path structure unrecognized.");
        return;
    }

    // Create a new file object for the target language
    // We need to parse the path correctly for the new file object
    const versionPrefix = `text/${selectedVersion}/`; 
    const relativePathNewLang = newFileKey.substring(versionPrefix.length);
    const newFileParts = relativePathNewLang.split('/');
    const newFileName = newFileParts[newFileParts.length -1];
    let newNamespace = relativePathNewLang.replace('.json', '');
    if (newFileParts.length > 1 && newFileParts[0].length === 2) { // lang/namespace.json or lang/file.json
        newNamespace = newFileParts.slice(1).join('/').replace('.json', '');
    }

    const newFileTarget = {
      ...selectedFile, // Copy other properties like original fileName if it makes sense
      key: newFileKey,
      lang: targetLang,
      path: relativePathNewLang, // e.g. fr/common.json
      fileName: newFileName, // e.g. common.json
      namespace: newNamespace // e.g. common
    };

    console.log("Switching language to:", targetLang, "New file target:", newFileTarget);

    if (isContentDirty) {
      setPendingAction({ action: actualFileClickLogic, args: [newFileTarget] });
      setIsWarningModalOpen(true);
    } else {
      actualFileClickLogic(newFileTarget);
    }
  };

  const handleLiveVersionChange = async (newLiveVersion) => {
    if (!newLiveVersion || newLiveVersion === liveVersion) return;

    if (!window.confirm(`Are you sure you want to change the live version from "${liveVersion}" to "${newLiveVersion}"? This will affect the live website immediately.`)) {
      return;
    }

    setIsUpdatingLiveVersion(true);
    setError(null);
    const cmsKey = localStorage.getItem('cmsKey');

    if (!cmsKey) {
      setError('CMS key not found. Please log in again.');
      setIsUpdatingLiveVersion(false);
      if (typeof onLogout === 'function') onLogout();
      return;
    }

    try {
      console.log(`[CMS] Updating live version from ${liveVersion} to ${newLiveVersion}`);
      
      const response = await fetch('/api/cms/update-live-version', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CMS-Key': cmsKey,
        },
        body: JSON.stringify({ newLiveVersion }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Error ${response.status}: Failed to update live version`);
      }

      setNotification({ 
        message: result.message || `Live version successfully updated to ${newLiveVersion}!`, 
        type: 'success', 
        visible: true 
      });
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 4000);

      // Refresh version data to reflect the changes
      const fetchVersions = async () => {
        try {
          const versionsResponse = await fetch('/api/cms/versions', {
            headers: { 'X-CMS-Key': cmsKey },
          });
          if (versionsResponse.ok) {
            const data = await versionsResponse.json();
            setVersionsData(data);
            console.log(`[CMS] Version data refreshed after live version update`);
          }
        } catch (err) {
          console.error("Error refreshing versions after live update:", err);
        }
      };
      
      fetchVersions();

    } catch (err) {
      console.error("Error updating live version:", err);
      setError(`Failed to update live version: ${err.message}`);
      setNotification({ 
        message: `Failed to update live version: ${err.message}`, 
        type: 'error', 
        visible: true 
      });
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 5000);
    } finally {
      setIsUpdatingLiveVersion(false);
    }
  };

  const handleExportVersion = async (versionToExport) => {
    if (!versionToExport) {
      setError('No version specified for export');
      return;
    }

    if (!window.confirm(`Export all files from version "${versionToExport}"? This will download a backup file containing all content.`)) {
      return;
    }

    setIsExporting(true);
    setError(null);
    const cmsKey = localStorage.getItem('cmsKey');

    if (!cmsKey) {
      setError('CMS key not found. Please log in again.');
      setIsExporting(false);
      if (typeof onLogout === 'function') onLogout();
      return;
    }

    try {
      console.log(`[CMS] Starting export for version: ${versionToExport}`);
      
      const response = await fetch('/api/cms/export-version', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CMS-Key': cmsKey,
        },
        body: JSON.stringify({ versionName: versionToExport }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Export failed with status ${response.status}`);
      }

      const exportData = await response.json();
      
      // Create and trigger download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${versionToExport}-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({ 
        message: `Successfully exported ${exportData.fileCount} files from ${versionToExport}!`, 
        type: 'success', 
        visible: true 
      });
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 4000);

      console.log(`[CMS] Export completed: ${exportData.fileCount} files downloaded`);

    } catch (err) {
      console.error("Error exporting version:", err);
      setError(`Failed to export version: ${err.message}`);
      setNotification({ 
        message: `Export failed: ${err.message}`, 
        type: 'error', 
        visible: true 
      });
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    // Attempt to scroll to the last focused field after content might have changed (e.g., lang switch)
    if (lastFocusedFieldForScroll && editingFileContent && !isLoadingFileContent) {
      const element = document.getElementById(lastFocusedFieldForScroll);
      if (element) {
        // Timeout to allow DOM to fully update after content change
        setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            console.log("Scrolled to element:", lastFocusedFieldForScroll);
            setLastFocusedFieldForScroll(null); // Reset after attempting scroll
        }, 100);
      }
    }
  }, [editingFileContent, isLoadingFileContent, lastFocusedFieldForScroll]);

  const styles = {
    pageContainer: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#f0f2f5',
      position: 'relative',
    },
    header: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      webkitBackdropFilter: 'blur(10px)',
      padding: '20px 30px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '80px',
      width: '100%',
      boxSizing: 'border-box',
      zIndex: 100,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      flex: '0 0 200px', // Fixed width for consistent positioning
    },
    headerCenter: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flex: '1', // Takes remaining space and centers content
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      flex: '0 0 200px', // Fixed width for consistent positioning
      justifyContent: 'flex-end',
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      padding: '4px',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.4)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    },
    tab: {
      padding: '10px 20px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: '#4a5568',
      fontWeight: '500',
      minWidth: '120px',
      textAlign: 'center',
    },
    activeTab: {
      backgroundColor: 'rgba(66, 153, 225, 0.9)',
      color: 'white',
      boxShadow: '0 2px 8px rgba(66, 153, 225, 0.3)',
      transform: 'translateY(-1px)',
    },
    liveVersionBox: {
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(12px)',
      webkitBackdropFilter: 'blur(12px)',
      padding: '8px 16px',
      borderRadius: '10px',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#2d3748',
      fontWeight: '500',
    },
    contentArea: {
      display: 'flex',
      flex: 1,
    },
    sidebar: {
      width: '280px',
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      backdropFilter: 'blur(20px)',
      webkitBackdropFilter: 'blur(20px)',
      padding: '20px',
      borderRight: '1px solid rgba(255, 255, 255, 0.4)',
      border: '1px solid rgba(255, 255, 255, 0.4)',
      borderRadius: '0 16px 16px 0',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s ease-in-out',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      maxHeight: 'calc(100vh - 100px)',
      overflowY: 'auto',
    },
    mainContent: {
      flexGrow: 1,
      padding: '25px 30px',
      overflowY: 'auto',
      backgroundColor: 'transparent',
      maxHeight: 'calc(100vh - 100px)',
    },
    sidebarTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '15px',
      paddingBottom: '10px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
    },
    versionList: { listStyle: 'none', padding: 0, margin: 0 },
    versionListItem: {
      padding: '12px 15px',
      marginBottom: '6px',
      borderRadius: '12px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontSize: '14px',
      color: '#4a5568',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      webkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
    },
    selectedVersionListItem: {
      backgroundColor: 'rgba(66, 153, 225, 0.3)',
      backdropFilter: 'blur(15px)',
      webkitBackdropFilter: 'blur(15px)',
      color: '#2b6cb0',
      fontWeight: '600',
      border: '1px solid rgba(66, 153, 225, 0.4)',
      boxShadow: '0 4px 16px rgba(66, 153, 225, 0.2)',
    },
    fileListItem: {
      padding: '10px 15px',
      marginBottom: '4px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '13px',
      color: '#4a5568',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transition: 'all 0.2s ease',
      hover: {
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
        transform: 'translateY(-1px)',
      }
    },
    selectedFileListItem: {
      backgroundColor: 'rgba(182, 218, 252, 0.4)',
      backdropFilter: 'blur(12px)',
      webkitBackdropFilter: 'blur(12px)',
      color: '#2c5282',
      fontWeight: '500',
      border: '1px solid rgba(182, 218, 252, 0.6)',
    },
    namespaceHeader: {
      fontSize: '12px',
      color: '#718096',
      textTransform: 'uppercase',
      marginTop: '15px',
      marginBottom: '5px',
      padding: '8px 15px 8px 10px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(5px)',
      webkitBackdropFilter: 'blur(5px)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      hover: {
        backgroundColor: 'rgba(255, 255, 255, 0.2)'
      }
    },
    collapseIcon: {
      transition: 'transform 0.2s ease-in-out',
    },
    sidebarBackButton: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 15px',
      marginBottom: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#3182ce',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      transition: 'all 0.2s ease',
      hover: {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'translateY(-1px)',
      }
    },
    versionTagsContainer: { display: 'flex', gap: '6px' }, 
    versionTag: {
      padding: '3px 7px',
      borderRadius: '6px',
      fontSize: '10px',
      color: 'white',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      lineHeight: '1',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
    },
    liveTag: { 
      backgroundColor: 'rgba(72, 187, 120, 0.9)',
      border: '1px solid rgba(72, 187, 120, 0.3)',
    },
    workingTag: { 
      backgroundColor: 'rgba(176, 190, 197, 0.9)', 
      color: '#2d3748',
      border: '1px solid rgba(176, 190, 197, 0.3)',
    },
    mainHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '25px',
      paddingBottom: '15px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#2d3748',
      margin: 0,
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
    },
    mainSectionTitle: {
      fontSize: '22px',
      fontWeight: '500',
      color: '#2d3748',
      margin: 0,
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
    },
    liveVersionDisplay: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      color: '#4a5568',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
    },
    logoutButton: {
      padding: '8px 16px',
      fontSize: '13px',
      color: 'white',
      backgroundColor: 'rgba(197, 48, 48, 0.8)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      border: '1px solid rgba(197, 48, 48, 0.3)',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    button: {
      padding: '10px 18px',
      fontSize: '15px',
      color: 'white',
      backgroundColor: 'rgba(66, 153, 225, 0.9)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      border: '1px solid rgba(66, 153, 225, 0.3)',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginRight: '10px',
      boxShadow: '0 4px 16px rgba(66, 153, 225, 0.2)',
    },
    actionCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      backdropFilter: 'blur(20px)',
      webkitBackdropFilter: 'blur(20px)',
      padding: '25px',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.4)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      marginBottom: '25px',
    },
    infoText: { 
      fontSize: '15px', 
      color: '#4a5568', 
      marginBottom: '15px', 
      lineHeight: '1.6',
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
    },
    loading: { 
      textAlign: 'center', 
      fontSize: '18px', 
      color: '#4a5568', 
      marginTop: '50px', 
      width: '100%',
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
    },
    error: { 
      backgroundColor: 'rgba(254, 202, 202, 0.8)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      color: '#c53030',
      border: '1px solid rgba(254, 202, 202, 0.4)',
      padding: '15px',
      borderRadius: '8px',
      textAlign: 'center',
      marginTop: '10px',
    },
    floatingActionBar: {
      position: 'fixed',
      bottom: '0',
      left: '280px',
      right: '0',
      padding: '15px 20px',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(20px)',
      webkitBackdropFilter: 'blur(20px)',
      boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      zIndex: 1000,
      transition: 'left 0.3s ease-in-out',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    actionBarButton: {
      padding: '8px 16px',
      fontSize: '14px',
      color: 'white',
      backgroundColor: 'rgba(66, 153, 225, 0.9)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      border: '1px solid rgba(66, 153, 225, 0.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginLeft: '10px',
    },
    saveButton: {
      backgroundColor: 'rgba(72, 187, 120, 0.9)',
      border: '1px solid rgba(72, 187, 120, 0.3)',
    },
    revertButton: {
      backgroundColor: 'rgba(255, 193, 7, 0.9)',
      color: '#744210',
      border: '1px solid rgba(255, 193, 7, 0.3)',
    },
    previewButton: {
      backgroundColor: 'rgba(23, 162, 184, 0.9)',
      border: '1px solid rgba(23, 162, 184, 0.3)',
    },
    disabledButton: {
      backgroundColor: 'rgba(108, 117, 125, 0.6)',
      opacity: 0.65,
      cursor: 'not-allowed',
    },
    notificationArea: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 2000,
    },
    notificationToast: {
      padding: '10px 20px',
      borderRadius: '8px',
      color: 'white',
      marginBottom: '10px',
      backdropFilter: 'blur(12px)',
      webkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      transition: 'opacity 0.5s ease-in-out',
      opacity: 1,
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    notificationSuccess: {
      backgroundColor: 'rgba(72, 187, 120, 0.9)',
    },
    notificationError: {
      backgroundColor: 'rgba(220, 53, 69, 0.9)',
    },
    previewModalBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1900,
    },
    previewModalContent: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      webkitBackdropFilter: 'blur(20px)',
      padding: '10px',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 16px 64px rgba(0, 0, 0, 0.3)',
      width: '90%',
      height: '90%',
      display: 'flex',
      flexDirection: 'column',
    },
    previewModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '10px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
      marginBottom: '10px',
    },
    previewModalTitle: {
      fontSize: '1.2em',
      margin: 0,
      color: '#2d3748',
    },
    previewModalCloseButton: {
      padding: '5px 10px',
      fontSize: '1.2em',
      cursor: 'pointer',
      border: 'none',
      backgroundColor: 'transparent',
      color: '#4a5568',
    },
    previewIframe: {
      flexGrow: 1,
      border: 'none',
      borderRadius: '8px',
    },
    warningModalBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2100, // Higher than notification and preview
    },
    warningModalContent: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      webkitBackdropFilter: 'blur(20px)',
      padding: '25px',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 16px 64px rgba(0, 0, 0, 0.3)',
      width: 'auto',
      maxWidth: '500px',
      textAlign: 'center',
    },
    warningModalTitle: {
      fontSize: '1.5em',
      fontWeight: '500',
      color: '#2d3748',
      marginBottom: '15px',
    },
    warningModalText: {
      fontSize: '1em',
      color: '#4a5568',
      marginBottom: '25px',
    },
    warningModalActions: {
      display: 'flex',
      justifyContent: 'space-around',
      gap: '10px',
    },
    warningModalButton: {
      padding: '10px 20px',
      fontSize: '1em',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      minWidth: '120px',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
    },
    liveVersionManagement: {
      backgroundColor: 'rgba(248, 250, 252, 0.3)',
      backdropFilter: 'blur(16px)',
      webkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    },
    liveVersionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
    },
    liveVersionControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      flexWrap: 'wrap',
    },
    liveVersionLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#4a5568',
    },
    liveVersionDropdown: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
      fontSize: '14px',
      minWidth: '120px',
      cursor: 'pointer',
      color: '#2d3748',
    },
    liveVersionInfo: {
      fontSize: '13px',
      color: '#718096',
      fontStyle: 'italic',
    },
    exportButton: {
      backgroundColor: 'rgba(138, 43, 226, 0.9)',
      border: '1px solid rgba(138, 43, 226, 0.3)',
    },
    backgroundImage: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: 'url(https://cdn.nexastaff.ai/back2.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      zIndex: -2,
    },
    backgroundOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(0.5px)',
      zIndex: -1,
    },
    sidebarFileExplorer: {
      width: '350px',
    },
    sidebarContentWrapper: {
      flexGrow: 1,
      overflowY: 'auto',
      paddingRight: '5px',
      marginRight: '-5px'
    },
  };

  if (isLoading && !versionsData) {
    return <div style={styles.loading}>Loading CMS Dashboard...</div>;
  }

  if (error && !versionsData) {
    return (
      <div style={{...styles.pageContainer, justifyContent: 'center', alignItems: 'center'}}>
        <div style={styles.error}>Error: {error}. Please try logging out and back in. 
            {typeof onLogout === 'function' && <button onClick={onLogout} style={{...styles.logoutButton, marginTop: '10px'}}>Logout</button>}
        </div>
      </div>
    );
  }
  
  const versionConfig = versionsData?.versionConfig || {};
  const discoveredVersionNames = versionsData?.discoveredVersionNames || [];
  const sortedDiscoveredVersions = [...discoveredVersionNames].sort((a, b) => b.localeCompare(a, undefined, {numeric: true}));

  const liveVersion = versionConfig.live || 'N/A';
  const workingVersion = versionConfig.working || 'N/A';

  const groupedFiles = versionFiles.reduce((acc, file) => {
    const langKey = file.lang || 'root';
    if (!acc[langKey]) acc[langKey] = [];
    acc[langKey].push(file);
    acc[langKey].sort((a, b) => a.path.localeCompare(b.path));
    return acc;
  }, {});

  const toggleLangSection = (langKey) => {
    setExpandedLangs(prev => ({...prev, [langKey]: !prev[langKey]}));
  };

  // Show analytics dashboard if analytics tab is active
        if (activeTab === 'analytics') {
        return <CMSAnalyticsDashboard onLogout={onLogout} onSwitchToTranslations={() => handleTabChange('translations')} />;
      }

  const renderFileTree = () => {
    if (isLoadingFiles) return <p style={{...styles.infoText, padding: '0 15px'}}>Loading files...</p>;
    if (!versionFiles.length && !isLoadingFiles && error && !selectedFile) { 
        return <p style={{...styles.error, width:'auto', fontSize: '12px', padding: '8px'}}>Error loading files: {error}</p>;
    }
    if (!versionFiles.length && !isLoadingFiles) return <p style={{...styles.infoText, padding: '0 15px'}}>No JSON files found in this version.</p>;

    return Object.entries(groupedFiles).map(([lang, filesInLang]) => (
        <div key={lang}>
            <h4 
                style={styles.namespaceHeader}
                onClick={() => toggleLangSection(lang)}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.namespaceHeader.hover.backgroundColor}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
                {lang === 'root' ? 'Root Files' : `Language: ${lang.toUpperCase()}`}
                <span style={{...styles.collapseIcon, transform: expandedLangs[lang] ? 'rotate(0deg)' : 'rotate(-90deg)'}}>▼</span>
            </h4>
            {expandedLangs[lang] && (
                <ul style={{...styles.versionList, marginLeft: '10px'}}>
                    {filesInLang.map(file => (
                        <li 
                            key={file.key}
                            style={{
                                ...styles.fileListItem,
                                ...(selectedFile?.key === file.key ? styles.selectedFileListItem : {}),
                            }}
                            onClick={() => handleFileClick(file)}
                            onMouseOver={(e) => { if (selectedFile?.key !== file.key) e.currentTarget.style.backgroundColor = styles.fileListItem.hover.backgroundColor; }}
                            onMouseOut={(e) => { if (selectedFile?.key !== file.key) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            {file.namespace} ({file.fileName})
                        </li>
                    ))}
                </ul>
            )}
        </div>
    ));
  };

  return (
    <div style={styles.pageContainer}>
      {/* Background Image and Overlay */}
      <div style={styles.backgroundImage}></div>
      <div style={styles.backgroundOverlay}></div>
      
      {/* Header with tabs */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>CMS Dashboard</h1>
        </div>
        
        <div style={styles.headerCenter}>
          <div style={styles.tabs}>
            <button 
              style={{
                ...styles.tab,
                ...(activeTab === 'translations' ? styles.activeTab : {})
              }}
              onClick={() => handleTabChange('translations')}
            >
              📝 Translations
            </button>
            <button 
              style={{
                ...styles.tab,
                ...(activeTab === 'analytics' ? styles.activeTab : {})
              }}
              onClick={() => handleTabChange('analytics')}
            >
              🎯 Analytics
            </button>
          </div>
        </div>
        
        <div style={styles.headerRight}>
          <div style={styles.liveVersionBox}>
            <span>Live:</span>
            <strong>{liveVersion}</strong>
            {liveVersion !== 'N/A' && <span style={{...styles.versionTag, ...styles.liveTag}}>Live</span>}
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div style={styles.contentArea}>
        <div style={{
          ...styles.sidebar,
          ...(selectedVersion ? styles.sidebarFileExplorer : {}),
        }}>
          {selectedVersion ? (
            <>
              <div 
                style={styles.sidebarBackButton}
                onClick={handleBackToVersions}
                onMouseOver={(e) => { 
                  e.currentTarget.style.backgroundColor = styles.sidebarBackButton.hover.backgroundColor;
                  e.currentTarget.style.borderColor = styles.sidebarBackButton.hover.borderColor; 
                }}
                onMouseOut={(e) => { 
                  e.currentTarget.style.backgroundColor = 'transparent'; 
                  e.currentTarget.style.borderColor = 'transparent'; 
                }}
              >
                <span style={{marginRight: '8px'}}>&#x2190;</span> Back to Versions
              </div>
              <h2 style={styles.sidebarTitle}>Files in: {selectedVersion}</h2>
              <div style={styles.sidebarContentWrapper}>
                  {renderFileTree()}
              </div>
            </>
          ) : (
            <>
              <h2 style={styles.sidebarTitle}>Versions</h2>
              {error && !isLoadingFiles && <p style={{...styles.error, width:'auto', fontSize: '12px', padding: '8px'}}>{error}</p>}
              <ul style={styles.versionList}>
                {sortedDiscoveredVersions.length > 0 ? sortedDiscoveredVersions.map(versionName => (
                  <li 
                    key={versionName} 
                    style={{
                      ...styles.versionListItem,
                      ...(selectedVersion === versionName ? styles.selectedVersionListItem : {}),
                    }}
                  >
                    <div 
                      style={{ flexGrow: 1, cursor: 'pointer' }}
                      onClick={() => handleVersionClick(versionName)}
                      onMouseOver={(e) => { 
                        if (selectedVersion !== versionName) e.currentTarget.parentElement.style.backgroundColor = '#f4f4f4'; 
                      }}
                      onMouseOut={(e) => { 
                        if (selectedVersion !== versionName) e.currentTarget.parentElement.style.backgroundColor = 'transparent'; 
                      }}
                    >
                      {versionName}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        style={{
                          padding: '4px 8px',
                          fontSize: '10px',
                          backgroundColor: 'rgba(138, 43, 226, 0.8)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportVersion(versionName);
                        }}
                        disabled={isExporting}
                        title={`Export ${versionName}`}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(138, 43, 226, 1)'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(138, 43, 226, 0.8)'}
                      >
                        📦
                      </button>
                      <div style={styles.versionTagsContainer}>
                        {versionName === liveVersion && <span style={{...styles.versionTag, ...styles.liveTag}}>Live</span>}
                        {versionName === workingVersion && <span style={{...styles.versionTag, ...styles.workingTag}}>Work</span>}
                      </div>
                    </div>
                  </li>
                )) : (
                  <p style={{...styles.infoText, padding: '0 15px'}}>
                    {isLoading ? 'Loading versions...' : 'No versions found in R2.'}
                  </p>
                )}
              </ul>
              <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #f0f0f0' }}>
                  {typeof onLogout === 'function' && 
                      <button 
                          onClick={onLogout} 
                          style={{...styles.logoutButton, width: '100%'}}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c62828' }
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.logoutButton.backgroundColor}
                      >Logout</button>}
              </div>
            </>
          )}
        </div>

        <div style={styles.mainContent}>
          <div style={styles.mainHeader}>
            <h2 style={styles.mainSectionTitle}>
              {selectedFile ? `Editing: ${selectedFile.path} (in ${selectedVersion})` 
                            : selectedVersion ? `Version Details: ${selectedVersion}` 
                                            : "Editing Nexa Staff Content"}
            </h2>
          </div>

          {error && !isCreating && <div style={{...styles.error, marginBottom: '15px'}}>{error}</div>}

          {!selectedVersion && (
            <div style={styles.actionCard}>
                <p style={styles.infoText}><strong>Default Working Version:</strong> {workingVersion !== 'N/A' ? workingVersion : "Not set"} 
                  {workingVersion !== 'N/A' && selectedVersion !== workingVersion && <span style={{...styles.versionTag, ...styles.workingTag}}>Work</span>}
                </p>
                <p style={styles.infoText}>Select a version from the list to manage its files, or create a new version.</p>
                <button 
                    onClick={handleCreateNewVersion} 
                    style={styles.button} 
                    disabled={isCreating || isLoading}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = isCreating || isLoading ? '' : '#40a9ff'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = isCreating || isLoading ? '' : styles.button.backgroundColor}
                >
                    {isCreating ? 'Creating Version...' : 'Create New Version'}
                </button>
            </div>
          )}

          {selectedVersion && !selectedFile && (
            <div style={styles.actionCard}> 
              <h3 style={{fontSize: '20px', fontWeight:'500', marginBottom:'15px'}}>
                  Files for: <span style={{color: '#1890ff'}}>{selectedVersion}</span>
                  {selectedVersion === liveVersion && <span style={{...styles.versionTag, ...styles.liveTag, marginLeft: '10px'}}>Live</span>}
                  {selectedVersion === workingVersion && <span style={{...styles.versionTag, ...styles.workingTag, marginLeft: '10px'}}>Work</span>}
              </h3>
               <p style={styles.infoText}>Select a JSON file from the sidebar to start editing its content.</p>
               
               <div style={{ marginBottom: '15px' }}>
                 <button 
                   onClick={() => handleExportVersion(selectedVersion)} 
                   style={{...styles.button, ...styles.exportButton}} 
                   disabled={isExporting || isLoadingFiles}
                   onMouseOver={(e) => e.currentTarget.style.backgroundColor = isExporting || isLoadingFiles ? '' : 'rgba(138, 43, 226, 0.7)'}
                   onMouseOut={(e) => e.currentTarget.style.backgroundColor = isExporting || isLoadingFiles ? '' : styles.exportButton.backgroundColor}
                 >
                   {isExporting ? 'Exporting...' : `📦 Export ${selectedVersion}`}
                 </button>
                 <span style={{fontSize: '13px', color: '#718096', marginLeft: '10px'}}>
                   Download all files as backup
                 </span>
               </div>
               
               {isLoadingFiles && <p style={styles.loading}>Loading files for {selectedVersion}...</p>}
               {!isLoadingFiles && versionFiles.length === 0 && !error && (
                  <p style={styles.infoText}>No JSON files found in this version or an error occurred.</p>
               )}
            </div>
          )}

          {selectedFile && (
              <div style={styles.actionCard}>
                   <h3 style={{fontSize: '20px', fontWeight:'500', marginBottom:'15px'}}>
                      Content for: <span style={{color: '#1890ff'}}>{selectedFile.path}</span>
                  </h3>
                  {isLoadingFileContent && <p style={styles.loading}>Loading content for {selectedFile.fileName}...</p>}
                  {!isLoadingFileContent && error && <div style={{...styles.error, marginBottom: '15px'}}>{error}</div>}
                  {!isLoadingFileContent && !error && editingFileContent && (
                      <div style={{ paddingBottom: '80px' }}>
                          <JsonEditorForm 
                              jsonData={editingFileContent} 
                              onDataChange={handleJsonDataChange} 
                              onFocusField={handleFocusField}
                          />
                      </div>
                  )}
                  {!isLoadingFileContent && !error && !editingFileContent && (
                      <p style={styles.infoText}>No content to display, or content could not be loaded.</p>
                  )}
              </div>
          )}

           {!selectedVersion && !selectedFile && !isLoading && (
              <div style={styles.actionCard}>
                  <div style={styles.liveVersionManagement}>
                    <h3 style={styles.liveVersionTitle}>
                      🌐 Live Version Management
                      {isUpdatingLiveVersion && <span style={{fontSize: '14px', color: '#666', fontWeight: 'normal'}}>Updating...</span>}
                    </h3>
                    <div style={styles.liveVersionControls}>
                      <span style={styles.liveVersionLabel}>Currently Live:</span>
                      <select 
                        style={{
                          ...styles.liveVersionDropdown,
                          ...(isUpdatingLiveVersion ? {opacity: 0.6, cursor: 'not-allowed'} : {})
                        }}
                        value={liveVersion}
                        onChange={(e) => handleLiveVersionChange(e.target.value)}
                        disabled={isUpdatingLiveVersion}
                      >
                        {sortedDiscoveredVersions.map(version => (
                          <option key={version} value={version}>
                            {version}
                            {version === liveVersion ? ' (Current)' : ''}
                            {version === workingVersion ? ' (Working)' : ''}
                          </option>
                        ))}
                      </select>
                      <span style={styles.liveVersionInfo}>
                        Changes take effect immediately on the live website
                      </span>
                    </div>
                  </div>
                  <p style={styles.infoText}>Select a version from the left sidebar to view and edit its content, or use the live version controls above to change which version is currently live.</p>
              </div>
          )}
        </div>
      </div>

      {selectedFile && (
        <div style={{
            ...styles.floatingActionBar,
            left: selectedVersion ? styles.sidebarFileExplorer.width : styles.sidebar.width, 
            // Adjust left based on whether file explorer (wider sidebar) is active
        }}>
            {selectedFile && selectedFile.lang && (
                 <button 
                    style={{...styles.actionBarButton, backgroundColor: '#546E7A', marginRight: 'auto'}}
                    onClick={handleLanguageSwitch}
                    disabled={isSaving} // Disable if any save operation is in progress
                >
                    Switch to {selectedFile.lang === 'en' ? 'FR' : 'EN'}
                </button>
            )}
            <button 
                style={{
                    ...styles.actionBarButton, 
                    ...styles.previewButton,
                    ...(isContentDirty ? styles.disabledButton : {}),
                }}
                onClick={handlePreviewClick}
                disabled={isContentDirty}
                title={isContentDirty ? "Preview disabled while you have unsaved changes" : "Preview current content"}
            >
                Preview
            </button>
            <button 
                style={{
                    ...styles.actionBarButton, 
                    ...styles.revertButton,
                    ...((!isContentDirty || isSaving) ? styles.disabledButton : {}),
                }}
                onClick={handleRevertChanges}
                disabled={!isContentDirty || isSaving}
            >
                Revert Changes
            </button>
            <button 
                style={{
                    ...styles.actionBarButton, 
                    ...styles.saveButton,
                    ...((!isContentDirty || isSaving) ? styles.disabledButton : {}),
                }}
                onClick={handleSaveChanges}
                disabled={!isContentDirty || isSaving}
            >
                {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
        </div>
      )}

      <div style={styles.notificationArea}>
        {notification.visible && (
            <div style={{
                ...styles.notificationToast,
                ...(notification.type === 'success' ? styles.notificationSuccess : {}),
                ...(notification.type === 'error' ? styles.notificationError : {}),
                opacity: notification.visible ? 1 : 0,
            }}>
                {notification.message}
            </div>
        )}
      </div>

      {isPreviewOpen && (
        <div style={styles.previewModalBackdrop} onClick={() => setIsPreviewOpen(false)}>
            <div style={styles.previewModalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.previewModalHeader}>
                    <h4 style={styles.previewModalTitle}>Previewing: {selectedFile?.path} (Version: {selectedVersion})</h4>
                    <button style={styles.previewModalCloseButton} onClick={() => setIsPreviewOpen(false)}>&times;</button>
                </div>
                <iframe 
                    src={previewUrl} 
                    key={previewUrl}
                    style={styles.previewIframe} 
                    title="Content Preview"
                ></iframe>
            </div>
        </div>
      )}

      {isWarningModalOpen && (
        <div style={styles.warningModalBackdrop}>
            <div style={styles.warningModalContent}>
                <h3 style={styles.warningModalTitle}>Unsaved Changes</h3>
                <p style={styles.warningModalText}>
                    You have unsaved changes. Would you like to save them before proceeding?
                </p>
                <div style={styles.warningModalActions}>
                    <button 
                        style={{...styles.warningModalButton, backgroundColor: '#28a745', color: 'white'}}
                        onClick={handleWarningModalSave} 
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save & Proceed'}
                    </button>
                    <button 
                        style={{...styles.warningModalButton, backgroundColor: '#ffc107', color: '#333'}}
                        onClick={handleWarningModalDiscard}
                    >
                        Discard & Proceed
                    </button>
                    <button 
                        style={{...styles.warningModalButton, backgroundColor: '#6c757d', color: 'white'}}
                        onClick={handleWarningModalCancel}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default CMSDashboardPage; 